import { Button, Spinner, toast } from '@heroui/react';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Canvas from '@/features/canvas-editor';
import { useKnowledgeBaseCanvasPersistence } from '@/components/canvas/document/persistence/useKnowledgeBaseCanvasPersistence';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';

export { DEFAULT_SLIDE_SIZE as SLIDE_SIZE } from '@/components/canvas/document/buildInitialCanvasContent';

/**
 * 知识库幻灯片路由页：`/knowledge-bases/slides/:slideId`、`:slideId/play`。
 * 新建在列表页 create 后带 id 进入；加载中保持画布壳子，避免整页 Spinner 闪烁。
 */
export function SlidesPage() {
  const { slideId } = useParams<{ slideId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const utils = trpc.useUtils();
  const isPlay = location.pathname.endsWith('/play');
  const currentUserId = useAppSelector((s) => s.auth.user?.id ?? null);

  useEffect(() => {
    if (!slideId) {
      navigate('/knowledge-bases', { replace: true });
    }
  }, [slideId, navigate]);

  const slideQuery = trpc.knowledgeBases.get.useQuery(
    { id: slideId! },
    { enabled: Boolean(slideId), staleTime: 0, refetchOnMount: 'always' },
  );

  const persistence = useKnowledgeBaseCanvasPersistence({
    fileFormat: 'slides',
    recordId: slideId,
    initialTitle: slideQuery.data?.title,
    initialContent: slideQuery.data?.content,
    enabled: Boolean(slideQuery.data),
  });

  const { flushSave, scheduleContentSave, handleRename, title, initialDocument } = persistence;

  const handleClose = useCallback(() => {
    useCanvasStore.getState().clearTableSession();
    void flushSave();
    void Promise.all([
      utils.knowledgeBases.listMy.invalidate(),
      utils.knowledgeBases.list.invalidate(),
      utils.knowledgeBases.listLatestCategoryDetails.invalidate(),
    ]);
    navigate('/knowledge-bases');
  }, [
    flushSave,
    navigate,
    utils.knowledgeBases.list,
    utils.knowledgeBases.listLatestCategoryDetails,
    utils.knowledgeBases.listMy,
  ]);

  const handleManualSave = useCallback(
    async (document: Parameters<typeof scheduleContentSave>[0]) => {
      const ok = await flushSave(document);
      if (ok) toast.success('已保存');
      return ok;
    },
    [flushSave],
  );

  if (!slideId) return null;

  const showLoadError = slideQuery.isError || (!slideQuery.isLoading && !slideQuery.data?.id);
  const showLoading = slideQuery.isLoading;
  const readonly = !currentUserId || slideQuery.data?.ownerId !== currentUserId;

  if (showLoadError) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center gap-2 text-sm text-destructive">
        <span>加载失败</span>
        <Button type="button" variant="secondary" size="sm" onPress={handleClose}>
          返回列表
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col">
      <Canvas
        mode="slide"
        editorKey={`slides-${slideId}`}
        onRequestClose={handleClose}
        initialDocument={showLoading ? undefined : initialDocument}
        fileName={title}
        onDocumentChange={scheduleContentSave}
        onRequestSave={handleManualSave}
        onRenameFile={handleRename}
        readonly={readonly}
        autoEnterFullscreen={isPlay}
        playConfig={
          isPlay
            ? { autoPlay: false }
            : null
        }
      />
      {showLoading ? (
        <div
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-background/60"
          aria-busy
          aria-label="加载幻灯片"
        >
          <Spinner className="size-8" />
        </div>
      ) : null}
    </div>
  );
}
